import { useContext, useEffect, useRef, useState } from 'react';

import ArtlistDataContext from '@/context/ArtlistDataContext';
import useStateHandler from '@/hooks/useStateHandler';
import autoCropImage from '@/pages/artwork/artwork-header/options/download-template/components/AutoCrop';
import PopupUpdateArtlistCover from '@/pages/artwork/artwork-header/options/download-template/components/PopupUpdateArtlistCover';
import UserService from '@/services/user.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';

import ArtlistConfirmationDialog from './confirmation-dialog/ArtlistConfirmationDialog';
import EditArtlistImage from './edit-artlist-image';
import SortArtworks from './sort-artworks';

import './index.css';

export default function EditArtlistForm({ open, close }) {
  const { artlistData, setArtlistData } = useContext(ArtlistDataContext);
  const { cacheHandler, stateHandler } = useStateHandler();
  const [formState, setFormState] = useState(artlistData);
  const [loading, setLoading] = useState(false); // For form submission
  const [coverUpdating, setCoverUpdating] = useState(false); // For cover update only
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [croppedUrl, setCroppedUrl] = useState(null);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [isCoverPopUpOpen, setIsCoverPopUpOpen] = useState(false);
  const [openArtworkImages, setOpenArtworkImages] = useState(false);
  // New state to store the artwork that is used as the cover source
  const [coverSourceArtwork, setCoverSourceArtwork] = useState(null);

  const scrollableDiv = useRef();
  const maxRows = 3;

  useEffect(() => {
    setFormState(artlistData);
  }, [artlistData]);

  const updateFormState = (key, value) => {
    setFormState(prevValue => Utils.addAttributeToObject(key, value, prevValue));
    setHasChanges(true);
  };

  const updateFormStateDescription = (value) => {
    const lines = value.split('\n');
    if (lines.length <= maxRows) {
      setFormState(prevValue => Utils.addAttributeToObject("artlist_description", value, prevValue));
      setHasChanges(true);
    }
  };

  const submitEditArtlist = async (event) => {
    event?.preventDefault();
    // Prevent submission if cover update is in progress.
    if (coverUpdating) {
      return;
    }
    setLoading(true);
    setError(null);
  
    const { response, data } = await UserService.editArtlist(formState);
  
    if (response.ok) {
      setArtlistData(data);
      cacheHandler.triggerAction("EDIT_ARTLIST");
      stateHandler.set("temporalPopup", {
        text: "Artlist information updated successfully", 
      });
      close(); // Close the popup on success
    } else if (response.status === 400) {
      setLoading(false);
      setError({ element: data.error_data.element, error_code: data.error_type });
    }
  
    setLoading(false);
  };

  const onConfirmCancel = () => {
    setFormState(artlistData);
    setShowExitConfirmation(false);
    close();
  };

  const onConfirmSaveAndExit = async () => {
    await submitEditArtlist();
    onConfirmCancel();
  };

  const canSave = !!formState.artlist_title;

  // Updated: handleCropComplete uses coverUpdating state.
  const handleCropComplete = async (croppedImage) => {
    try {
      setCoverUpdating(true);
      setIsCoverPopUpOpen(false);
      setOpenArtworkImages(false);
      console.log("handleCropComplete: coverSourceArtwork =", coverSourceArtwork);
      const srcArtwork = coverSourceArtwork;
      setImageToCrop(null);
    
      const { data } = await UserService.uploadImage(croppedImage);
    
      if (data && data.image_id) {
        // Save cover along with the source artwork's _id.
        updateFormState("artlist_image", { 
          image_id: data.image_id, 
          source_artwork_id: srcArtwork ? srcArtwork._id : null 
        });        
        setCroppedUrl(URL.createObjectURL(croppedImage));
      } else {
        console.warn("Upload response did not return a valid image_id.");
      }
    } catch (error) {
      console.error("Error in uploading cropped image:", error);
    } finally {
      setCoverUpdating(false);
    }
  };

  // Updated: Always update coverSourceArtwork with the selected artwork.
  const onImageToCropClick = (artworkImage) => {
    console.log("Selected artwork for cover:", artworkImage);
    setCoverSourceArtwork(artworkImage);    
    if (!formState.artlist_image) {
      console.log('onImageToCropClick: No cover exists. Starting auto-crop.');
      autoCropImage(artworkImage)
        .then((croppedBlob) => {
          console.log('onImageToCropClick: Auto-crop successful, passing blob to handleCropComplete.');
          handleCropComplete(croppedBlob);
        })
        .catch((error) => {
          console.error('onImageToCropClick: Auto-crop error:', error);
        });
    } else {
      console.log('onImageToCropClick: Cover already exists, opening manual cropping popup.');
      setImageToCrop(artworkImage);
      setIsCoverPopUpOpen(true);
    }
  };

  return (
    <>
      <GenericPopup className="edit-artlist-popup" open={open} opacity>
        <div className="edit-artlist-content__outter-container">
          <div className="edit-artlist-content__container">
            <form 
              onSubmit={submitEditArtlist} 
              className="edit-artlist-content__scrollable-container remove-scrollbar" 
              ref={scrollableDiv}
            >
              <div className="edit-artlist-content__header">
                <ContinueButton 
                  loading={loading} 
                  turnOff={!canSave || coverUpdating} 
                  continueClass={false}
                  className="text-custom__component edit-artlist-popup__submit-form-button element-non-selectable medium"
                >
  Save changes
                </ContinueButton>

                <Text className="edit-artlist-content__header-text" medium>
                  Edit artlist
                </Text>
                <XIcon 
                  className="edit-artlist-content__close" 
                  onClick={() => {
                    if (loading || coverUpdating) return;
                    if (hasChanges) {
                      setShowExitConfirmation(true);
                    } else {
                      close();
                    }
                  }}
                />

              </div>

              {/* Cover image editor */}
              <EditArtlistImage
                formState={formState}
                scrollableDiv={scrollableDiv}
                readOnly={loading || coverUpdating}
                croppedUrl={croppedUrl}
                loading={coverUpdating}      // Use coverUpdating for the cover image spinner
                onImageToCropClick={onImageToCropClick}
                openArtworkImages={openArtworkImages}
                setOpenArtworkImages={setOpenArtworkImages}
              />

              {/* Title input */}
              <PublicFormInput 
                readOnly={loading || coverUpdating} 
                error={error} 
                placeholder="Title" 
                element="artlist_title" 
                maxLength="46"
                margin
                value={formState.artlist_title}
                onChange={e => updateFormState("artlist_title", e.target.value)}
              />

              {/* Description input */}
              <PublicFormInput 
                readOnly={loading || coverUpdating} 
                error={error} 
                placeholder="Description" 
                element="artlist_description" 
                type="textarea"
                maxLength="256"
                rows={maxRows}
                margin
                value={formState.artlist_description}
                onChange={e => updateFormStateDescription(e.target.value)}
              />

              {/* Artworks list */}
              <div className="edit-artlist-content__artlist-artworks-container">
                { formState.artlist_artworks.length === 0 ? 
                  <Text className="edit-artlist-content__artlist-empty-text" medium paragraph>
                      No artworks added yet.
                  </Text>
                  :
                  <SortArtworks 
                    formState={formState} 
                    updateFormState={updateFormState}
                    setCoverUpdating={setCoverUpdating}  // Pass down the setter
                  />
                }
              </div>
            </form>
          </div>
        </div>
      </GenericPopup>
      
      {/* Crop popup for manual cropping */}
      <PopupUpdateArtlistCover
        open={isCoverPopUpOpen}
        image={imageToCrop}
        exitCrop={() => { 
          setIsCoverPopUpOpen(false);
          setImageToCrop(null);
        }}
        setCroppedFile={handleCropComplete}
        setCroppedUrl={setCroppedUrl}
        cropShape="rect"
        aspect={1}
      />

      {/* Confirmation dialog for unsaved changes */}
      <ArtlistConfirmationDialog 
        open={showExitConfirmation} 
        close={() => setShowExitConfirmation(false)}
        onConfirmSaveAndExit={onConfirmSaveAndExit}
        onConfirmCancel={onConfirmCancel}
        artlist={formState}
        canSave={canSave && !coverUpdating} // Disable if cover is still updating.
      />
    </>
  );
}
